import { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, EyeOff, Star, Loader2, ExternalLink, Package, ChevronLeft, ChevronRight, Upload, Grid3x3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listStoreProducts, deleteStoreProduct, toggleActive, toggleFeatured, fileUrl } from '../lib/storeApi';
import StoreProductForm from './StoreProductForm';

export default function StoreManager() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStoreProducts({ page, perPage: 20, search, platform, status });
      setProducts(res.items);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch (e) {
      console.error(e);
      setProducts([]);
    } finally { setLoading(false); }
  }, [page, search, platform, status]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (p) => {
    if (!window.confirm('Delete "' + p.title + '"?')) return;
    setBusyId(p.id);
    try { await deleteStoreProduct(p.id); await load(); }
    catch (e) { alert('Delete failed: ' + e.message); }
    finally { setBusyId(null); }
  };
  const handleToggleActive = async (p) => {
    setBusyId(p.id);
    try { await toggleActive(p.id, !p.isActive); setProducts(prev => prev.map(x => x.id === p.id ? { ...x, isActive: !p.isActive } : x)); }
    finally { setBusyId(null); }
  };
  const handleToggleFeatured = async (p) => {
    setBusyId(p.id);
    try { await toggleFeatured(p.id, !p.isFeatured); setProducts(prev => prev.map(x => x.id === p.id ? { ...x, isFeatured: !p.isFeatured } : x)); }
    finally { setBusyId(null); }
  };

  if (editing) {
    return <StoreProductForm product={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-amber-500" /> Store Manager
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Affiliate products shown on the public store page.</p>
        </div>
        <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-4 py-2.5 rounded-xl shadow-sm">
          <Plus size={16} /> Add Product
        </button>
        <button onClick={() => navigate('/store/bulk')}
          className="inline-flex items-center gap-2 bg-white border-2 border-indigo-500 hover:bg-indigo-50 text-indigo-600 font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all">
          <Upload size={16} /> Bulk Upload
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 outline-none" />
        </div>
        <select value={platform} onChange={e => { setPlatform(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All platforms</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="meesho">Meesho</option>
          <option value="myntra">Myntra</option>
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="featured">Featured</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">{totalItems} products</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-amber-500" /></div>
        ) : products.length === 0 ? (
          <div className="py-20 text-center">
            <Package size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-900 font-bold mb-1">No products yet</p>
            <button onClick={() => setEditing('new')} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-xl mt-3">Add Product</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left p-3 font-semibold">Product</th>
                  <th className="text-left p-3 font-semibold">Platform</th>
                  <th className="text-left p-3 font-semibold">Price</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const img = p.images && p.images[0] ? fileUrl(p, p.images[0]) : '';
                  return (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                      <td className="p-3">
                        <div className="flex items-center gap-3 max-w-md">
                          <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden shrink-0">
                            {img && <img src={img} alt="" className="w-full h-full object-contain p-1" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 line-clamp-1">{p.title}</p>
                            {p.brand && <p className="text-xs text-gray-400">{p.brand}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3"><span className="capitalize text-xs font-medium px-2 py-1 rounded bg-gray-100">{p.platform}</span></td>
                      <td className="p-3">
                        <div className="font-bold text-gray-900">₹{p.price}</div>
                        {p.mrp > p.price && <div className="text-xs text-gray-400 line-through">₹{p.mrp}</div>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <button onClick={() => handleToggleActive(p)} disabled={busyId === p.id}
                            className={'inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ' + (p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                            {p.isActive ? <><Eye size={11} /> Active</> : <><EyeOff size={11} /> Hidden</>}
                          </button>
                          {p.isFeatured && <span className="text-[10px] font-bold text-amber-600">⭐ Featured</span>}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleToggleFeatured(p)} className="p-2 rounded-lg hover:bg-amber-50 text-amber-500">
                            <Star size={14} fill={p.isFeatured ? 'currentColor' : 'none'} />
                          </button>
                          <a href={p.affiliateUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-blue-50 text-blue-500">
                            <ExternalLink size={14} />
                          </a>
                          <button onClick={() => setEditing(p)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(p)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-5">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
